// app/javascript/controllers/payout-choice-controller.js
import { Controller } from "@hotwired/stimulus";
import JustValidate from "just-validate";
import Swal from "sweetalert2";
import { gsap } from "gsap"

export default class extends Controller {
    static targets = [
        "grid", "option", "submit", "brandCode",
        "paypalFields", "paypalEmail", "paypalEmailConfirm",
        "chequeFields", "chequeAttention", "chequeStreet", "chequeCity", "chequeProvince", "chequePostal",
        "interacFields", "interacEmail", "interacEmailConfirm",
        "fieldsWrapper"
    ];

    connect() {
        this.currentMethod =
            this.element.querySelector('input[name="payout_method"]:checked')?.value || "amazon";

        // If a gift card is already selected (e.g., back/forward nav), seed the hidden field
        if (this.currentMethod === "giftcard") {
            const checked = this.element.querySelector('input[name="payout_method"][value="giftcard"]:checked');
            const label = checked?.closest('label[data-brand-code]');
            if (label && this.hasBrandCodeTarget) {
                this.brandCodeTarget.value = label.dataset.brandCode || "";
            }
        }

        this._refreshUI();

        this.validator = new JustValidate(this.element, {
            validateBeforeSubmitting: false,
            lockForm: false,
            focusInvalidField: true,
            errorFieldCssClass: "is-invalid",
        });

        this._setupValidationRules();

        this.validator.onSuccess(() => {
            const meta = this._currentSelectionMeta?.();

            // Safety: if somehow nothing is selected, fall back to old behavior
            if (!meta) {
                this.submitTarget.disabled = true;
                this.submitTarget.textContent = "Processing...";
                this.element.submit();
                return;
            }

            // If SweetAlert2 is available, show confirmation dialog
            if (window.Swal) {
                window.Swal.fire({
                    title: 'Confirm your payout choice',
                    html: `<strong>${meta.title}</strong>.<BR><BR><strong>This choice cannot be changed once processed.</strong>`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Yes, confirm',
                    cancelButtonText: 'Go back',
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.submitTarget.disabled = true;
                        this.submitTarget.textContent = 'Processing...';
                        this.element.submit();
                    }
                });
            } else {
                // Fallback: behave exactly as before if SweetAlert is not available
                this.submitTarget.disabled = true;
                this.submitTarget.textContent = "Processing...";
                this.element.submit();
            }
        });
    }

    async submit(event) {
        event.preventDefault();
        const isValid = await this.validator.validate();
        if (isValid) {
            this.submitTarget.disabled = true;
            this.submitTarget.textContent = "Processing...";
            this.element.submit();
        }
    }

    select(event) {
        this.currentMethod = this.element.querySelector('input[name="payout_method"]:checked')?.value || null;

        // If a gift card was selected, set the shared hidden field to that card’s brand code
        if (this.currentMethod === "giftcard" && this.hasBrandCodeTarget) {
            const label = event.currentTarget.closest('label[data-brand-code]');
            this.brandCodeTarget.value = label?.dataset.brandCode || "";
        }

        this._refreshUI();

        // Rebuild validation rules for the newly visible fields
        this.validator.destroy();
        this.validator = new JustValidate(this.element, {
            validateBeforeSubmitting: false,
            lockForm: false,
            focusInvalidField: true,
            errorFieldCssClass: "is-invalid",
        });
        this._setupValidationRules();

        this.validator.onSuccess(() => {
            const meta = this._currentSelectionMeta?.();

            if (!meta) {
                this.submitTarget.disabled = true;
                this.submitTarget.textContent = "Processing...";
                this.element.submit();
                return;
            }

            if (window.Swal) {
                window.Swal.fire({
                    title: 'Confirm your payout choice',
                    html: `<strong>${meta.title}</strong>.<BR><BR><strong>This choice cannot be changed once processed.</strong>`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Yes, confirm',
                    cancelButtonText: 'Go back',
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.submitTarget.disabled = true;
                        this.submitTarget.textContent = 'Processing...';
                        this.element.submit();
                    }
                });
            } else {
                this.submitTarget.disabled = true;
                this.submitTarget.textContent = "Processing...";
                this.element.submit();
            }
        });

        // After changing selection, if the method needs extra fields,
        // scroll them into view and focus the first input (helps a ton on mobile).
        this._scrollToFieldsAndFocus();
    }

    // ---- private

    _currentSelectionMeta() {
        const checked = this.element.querySelector('input[name="payout_method"]:checked');
        if (!checked) return null;

        const label =
            checked.closest('label.payout-option') ||
            checked.closest('label[data-payout-choice-target="option"]');
        if (!label) return null;

        const titleEl = label.querySelector('.title');
        const descEl  = label.querySelector('.desc');

        return {
            method: checked.value,
            title: titleEl ? titleEl.textContent.trim() : checked.value,
            desc: descEl ? descEl.textContent.trim() : '',
        };
    }

    _setupValidationRules() {
        const paypalVisible = () =>
            this.hasPaypalFieldsTarget && this._isVisible(this.paypalFieldsTarget);
        const chequeVisible = () =>
            this.hasChequeFieldsTarget && this._isVisible(this.chequeFieldsTarget);
        const interacVisible = () =>
            this.hasInteracFieldsTarget && this._isVisible(this.interacFieldsTarget);

        const reqIfVisible = (msg, isVisibleFn) => ({
            validator: (value) => !isVisibleFn() || !!(value && value.trim().length > 0),
            errorMessage: msg,
        });

        // NEW: require at least one payout method to be selected
        if (this.element.querySelector('input[name="payout_method"]')) {
            this.validator.addField('input[name="payout_method"]', [
                {
                    validator: () =>
                        !!this.element.querySelector('input[name="payout_method"]:checked'),
                    errorMessage: 'Please choose a payment option',
                },
            ], {
                errorsContainer: '#payout-method-error'
            });
        }

        // Only register rules for inputs that exist
        if (this.element.querySelector('[name="paypal[email]"]')) {
            this.validator
                .addField('[name="paypal[email]"]', [
                    reqIfVisible('PayPal email is required', paypalVisible),
                    {
                        validator: (value) => !paypalVisible() || /\S+@\S+\.\S+/.test(value),
                        errorMessage: 'Enter a valid email',
                    },
                ]);
        }

        if (this.element.querySelector('[name="paypal[email_confirmation]"]')) {
            this.validator
                .addField('[name="paypal[email_confirmation]"]', [
                    reqIfVisible('Please confirm your PayPal email', paypalVisible),
                    {
                        validator: (value) => {
                            if (!paypalVisible()) return true;
                            const email = this.element.querySelector('[name="paypal[email]"]')?.value || '';
                            return value.trim() === email.trim();
                        },
                        errorMessage: 'Emails do not match',
                    },
                ]);
        }

        // Interac email fields
        if (this.element.querySelector('[name="interac[email]"]')) {
            this.validator
                .addField('[name="interac[email]"]', [
                    reqIfVisible('Interac email is required', interacVisible),
                    {
                        validator: (value) =>
                            !interacVisible() || /\S+@\S+\.\S+/.test(value),
                        errorMessage: 'Enter a valid email',
                    },
                ]);
        }

        if (this.element.querySelector('[name="interac[email_confirmation]"]')) {
            this.validator
                .addField('[name="interac[email_confirmation]"]', [
                    reqIfVisible('Please confirm your Interac email', interacVisible),
                    {
                        validator: (value) => {
                            if (!interacVisible()) return true;
                            const email = this.element.querySelector('[name="interac[email]"]')?.value || '';
                            return value.trim() === email.trim();
                        },
                        errorMessage: 'Emails do not match',
                    },
                ]);
        }

        if (this.element.querySelector('[name="cheque[attention]"]')) {
            this.validator.addField('[name="cheque[attention]"]',
                [reqIfVisible('Attention is required', chequeVisible)]);
        }
        if (this.element.querySelector('[name="cheque[street_address]"]')) {
            this.validator.addField('[name="cheque[street_address]"]',
                [reqIfVisible('Street is required', chequeVisible)]);
        }
        if (this.element.querySelector('[name="cheque[city]"]')) {
            this.validator.addField('[name="cheque[city]"]',
                [reqIfVisible('City is required', chequeVisible)]);
        }
        if (this.element.querySelector('[name="cheque[postal_zip]"]')) {
            this.validator.addField('[name="cheque[postal_zip]"]',
                [reqIfVisible('Postal code is required', chequeVisible)]);
        }

        // Always required
        if (this.element.querySelector('[name="agree"]')) {
            this.validator.addField('[name="agree"]', [
                { rule: 'required', errorMessage: 'You must confirm to close the party' },
            ]);
        }
    }

    _scrollToFieldsAndFocus() {
        console.log("Scrl to")
        // Only care about methods that actually have extra fields
        let fieldContainer = null;
        let firstInput = null;

        if (this.currentMethod === 'interac' && this.hasInteracFieldsTarget) {
            fieldContainer = this.interacFieldsTarget;
            if (this.hasInteracEmailTarget) firstInput = this.interacEmailTarget;
        } else if (this.currentMethod === 'paypal' && this.hasPaypalFieldsTarget) {
            fieldContainer = this.paypalFieldsTarget;
            if (this.hasPaypalEmailTarget) firstInput = this.paypalEmailTarget;
        } else if (this.currentMethod === 'cheque' && this.hasChequeFieldsTarget) {
            fieldContainer = this.chequeFieldsTarget;
            if (this.hasChequeAttentionTarget) firstInput = this.chequeAttentionTarget;
        }

        if (!fieldContainer) return;

        // Scroll the whole fields area into view (wrapper is nicer on mobile)
        const scrollTarget = this.hasFieldsWrapperTarget
            ? this.fieldsWrapperTarget
            : fieldContainer;

        scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Focus the logical first field after a tiny delay so the browser
        // has time to bring it into view
        if (firstInput) {
            setTimeout(() => firstInput.focus(), 500);
        }
    }

    _refreshUI() {
        // highlight selected radio label
        this.optionTargets.forEach(label => {
            const input = label.querySelector('input[type="radio"]');
            label.classList.toggle('selected', !!input?.checked);
        });

        // Show/hide field groups only if targets exist
        if (this.hasPaypalFieldsTarget) {
            this._show(this.paypalFieldsTarget, this.currentMethod === 'paypal');
        }
        if (this.hasChequeFieldsTarget) {
            this._show(this.chequeFieldsTarget, this.currentMethod === 'cheque');
        }
        if (this.hasInteracFieldsTarget) {
            this._show(this.interacFieldsTarget, this.currentMethod === 'interac');
        }
    }

    _show(node, on) {
        if (!node) return;
        const wasHidden = node.classList.contains('d-none');

        node.classList.toggle('d-none', !on);

        // Optional: small GSAP animation when revealing a block
        if (on && wasHidden && window.gsap) {
            window.gsap.fromTo(
                node,
                { opacity: 0, y: -8 },
                { opacity: 1, y: 0, duration: 0.25, ease: 'power1.out' }
            );
        }
    }

    _isVisible(node) {
        return !!node && !node.classList.contains('d-none');
    }
}